/**
 * RettVerk 1.1 - Privacy-First Onboarding Service
 * Håndterer første gangs oppsett av lokal lagring og kryptering
 * Sist oppdatert: 20. februar 2026 — versjon 2.0.0
 * 
 * ABSOLUTTE PRINSIPPER:
 * - Ingen dokumenter skal noen gang lastes opp til server
 * - Ingen søk skal noen gang lastes opp til server
 * - Ingen krypteringsnøkler skal noen gang forlate enheten
 * - Ingen automatisk synkronisering mellom enheter
 * - Backend håndterer kun metadata
 * - Lokal lagring er alltid primærkilde for innhold
 */

import {
  checkStorageAvailability,
  initLocalStorage,
  getStorageStats,
  saveSetting,
  getSetting
} from './localStorageService';

import {
  isEncryptionSetup,
  getEncryptionStatus
} from './encryptionService';

const ONBOARDING_DB_NAME = 'RettVerkOnboarding';
const ONBOARDING_VERSION = 1;
const CURRENT_DB_VERSION = 3; // Versjon 3: Full encryption integration

// Onboarding status constants
export const ONBOARDING_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  STORAGE_UNAVAILABLE: 'storage_unavailable',
  ENCRYPTION_REQUIRED: 'encryption_required'
};

// Onboarding steps
export const ONBOARDING_STEPS = {
  PRIVACY_INTRO: 'privacy_intro',
  STORAGE_CHECK: 'storage_check',
  ENCRYPTION_SETUP: 'encryption_setup',
  LIBRARY_INIT: 'library_init',
  METADATA_SYNC: 'metadata_sync',
  COMPLETE: 'complete'
};

/**
 * Check if onboarding has been completed on this device
 */
export const checkOnboardingStatus = async () => {
  try {
    // Check if storage is available
    const storageAvailable = await checkStorageAvailability();
    
    if (!storageAvailable) {
      return {
        status: ONBOARDING_STATUS.STORAGE_UNAVAILABLE,
        needsOnboarding: false,
        canProceedWithLimitedFunctionality: true,
        message: 'Lokal lagring er ikke tilgjengelig på denne enheten.'
      };
    }

    // Check if onboarding has been completed
    const onboardingComplete = await getSetting('onboarding_completed');
    const storedDbVersion = await getSetting('db_version');
    
    // Re-run onboarding if DB version changed
    if (onboardingComplete && storedDbVersion !== CURRENT_DB_VERSION) {
      return {
        status: ONBOARDING_STATUS.NOT_STARTED,
        needsOnboarding: true,
        reason: 'db_version_changed',
        message: 'Database-versjonen har endret seg. Onboarding må kjøres på nytt.'
      };
    }

    if (onboardingComplete) {
      return {
        status: ONBOARDING_STATUS.COMPLETED,
        needsOnboarding: false,
        completedAt: await getSetting('onboarding_completed_at')
      };
    }

    return {
      status: ONBOARDING_STATUS.NOT_STARTED,
      needsOnboarding: true,
      reason: 'first_time'
    };
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return {
      status: ONBOARDING_STATUS.FAILED,
      needsOnboarding: true,
      error: error.message
    };
  }
};

/**
 * Step 1: Initialize storage and check availability
 */
export const initializeStorage = async (onProgress) => {
  try {
    onProgress?.({ step: ONBOARDING_STEPS.STORAGE_CHECK, status: 'in_progress' });
    
    const result = await initLocalStorage();
    
    if (!result.available) {
      onProgress?.({ step: ONBOARDING_STEPS.STORAGE_CHECK, status: 'failed', error: result.message });
      return {
        success: false,
        error: result.message,
        canContinue: true // Can continue with limited functionality
      };
    }

    // Save storage status
    await saveSetting('storage_initialized', true);
    await saveSetting('storage_initialized_at', new Date().toISOString());
    
    onProgress?.({ step: ONBOARDING_STEPS.STORAGE_CHECK, status: 'completed' });
    
    return {
      success: true,
      storageAvailable: true
    };
  } catch (error) {
    onProgress?.({ step: ONBOARDING_STEPS.STORAGE_CHECK, status: 'failed', error: error.message });
    return {
      success: false,
      error: error.message,
      canContinue: true
    };
  }
};

/**
 * Step 2: Setup encryption keys
 */
export const setupEncryption = async (onProgress) => {
  try {
    onProgress?.({ step: ONBOARDING_STEPS.ENCRYPTION_SETUP, status: 'in_progress' });
    
    // Check if Web Crypto API is available
    if (!window.crypto || !window.crypto.subtle) {
      onProgress?.({ step: ONBOARDING_STEPS.ENCRYPTION_SETUP, status: 'failed', error: 'Kryptering ikke støttet' });
      return {
        success: false,
        error: 'Web Crypto API er ikke tilgjengelig i denne nettleseren.',
        canContinue: true
      };
    }

    // Generate encryption key (this is also done in localStorageService, but we verify here)
    const key = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false, // Not extractable - key never leaves device
      ['encrypt', 'decrypt']
    );

    if (!key) {
      throw new Error('Kunne ikke generere krypteringsnøkkel');
    }

    // Save encryption status (not the key itself)
    await saveSetting('encryption_enabled', true);
    await saveSetting('encryption_algorithm', 'AES-GCM-256');
    await saveSetting('encryption_setup_at', new Date().toISOString());
    
    onProgress?.({ step: ONBOARDING_STEPS.ENCRYPTION_SETUP, status: 'completed' });
    
    return {
      success: true,
      encryptionEnabled: true,
      algorithm: 'AES-GCM-256'
    };
  } catch (error) {
    onProgress?.({ step: ONBOARDING_STEPS.ENCRYPTION_SETUP, status: 'failed', error: error.message });
    return {
      success: false,
      error: error.message,
      canContinue: true
    };
  }
};

/**
 * Step 3: Initialize local library structure
 */
export const initializeLibrary = async (onProgress) => {
  try {
    onProgress?.({ step: ONBOARDING_STEPS.LIBRARY_INIT, status: 'in_progress' });
    
    // Initialize documents library
    await saveSetting('library_initialized', true);
    await saveSetting('library_initialized_at', new Date().toISOString());
    await saveSetting('library_document_count', 0);
    
    // Initialize search history
    await saveSetting('search_initialized', true);
    await saveSetting('search_initialized_at', new Date().toISOString());
    await saveSetting('search_count', 0);
    
    // Save status information
    const statusInfo = {
      fs_api_supported: 'showDirectoryPicker' in window,
      local_folder_connected: false,
      encryption_enabled: true,
      db_version: CURRENT_DB_VERSION,
      initialized_at: new Date().toISOString()
    };
    
    await saveSetting('status_info', statusInfo);
    
    onProgress?.({ step: ONBOARDING_STEPS.LIBRARY_INIT, status: 'completed' });
    
    return {
      success: true,
      statusInfo
    };
  } catch (error) {
    onProgress?.({ step: ONBOARDING_STEPS.LIBRARY_INIT, status: 'failed', error: error.message });
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Step 4: Sync metadata with backend (first time only)
 */
export const syncMetadataWithBackend = async (userId, token, onProgress) => {
  try {
    onProgress?.({ step: ONBOARDING_STEPS.METADATA_SYNC, status: 'in_progress' });
    
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    
    // Send only metadata to backend - NEVER document content or search queries
    const response = await fetch(`${API}/users/onboarding-complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        onboarding_status: 'completed',
        device_id: await getDeviceId(),
        storage_available: true,
        encryption_enabled: true,
        db_version: CURRENT_DB_VERSION,
        completed_at: new Date().toISOString()
        // NO document content
        // NO search queries
        // NO encryption keys
      })
    });

    if (!response.ok) {
      // Don't fail onboarding if backend sync fails
      console.warn('Backend sync failed, continuing anyway');
    }

    onProgress?.({ step: ONBOARDING_STEPS.METADATA_SYNC, status: 'completed' });
    
    return {
      success: true,
      synced: response.ok
    };
  } catch (error) {
    // Don't fail onboarding if backend sync fails
    console.warn('Backend sync error:', error);
    onProgress?.({ step: ONBOARDING_STEPS.METADATA_SYNC, status: 'completed' });
    
    return {
      success: true, // Continue anyway
      synced: false,
      error: error.message
    };
  }
};

/**
 * Step 5: Complete onboarding
 */
export const completeOnboarding = async (onProgress) => {
  try {
    onProgress?.({ step: ONBOARDING_STEPS.COMPLETE, status: 'in_progress' });
    
    // Mark onboarding as completed
    await saveSetting('onboarding_completed', true);
    await saveSetting('onboarding_completed_at', new Date().toISOString());
    await saveSetting('db_version', CURRENT_DB_VERSION);
    
    onProgress?.({ step: ONBOARDING_STEPS.COMPLETE, status: 'completed' });
    
    return {
      success: true,
      completedAt: new Date().toISOString()
    };
  } catch (error) {
    onProgress?.({ step: ONBOARDING_STEPS.COMPLETE, status: 'failed', error: error.message });
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Run full onboarding sequence
 */
export const runOnboarding = async (userId, token, onProgress, onStepChange) => {
  const results = {
    storage: null,
    encryption: null,
    library: null,
    metadata: null,
    complete: null
  };

  try {
    // Step 1: Initialize storage
    onStepChange?.(ONBOARDING_STEPS.STORAGE_CHECK);
    results.storage = await initializeStorage(onProgress);
    
    if (!results.storage.success && !results.storage.canContinue) {
      return { success: false, results, failedAt: ONBOARDING_STEPS.STORAGE_CHECK };
    }

    // Step 2: Setup encryption
    onStepChange?.(ONBOARDING_STEPS.ENCRYPTION_SETUP);
    results.encryption = await setupEncryption(onProgress);
    
    // Step 3: Initialize library
    onStepChange?.(ONBOARDING_STEPS.LIBRARY_INIT);
    results.library = await initializeLibrary(onProgress);
    
    if (!results.library.success) {
      return { success: false, results, failedAt: ONBOARDING_STEPS.LIBRARY_INIT };
    }

    // Step 4: Sync metadata with backend
    onStepChange?.(ONBOARDING_STEPS.METADATA_SYNC);
    results.metadata = await syncMetadataWithBackend(userId, token, onProgress);
    
    // Step 5: Complete onboarding
    onStepChange?.(ONBOARDING_STEPS.COMPLETE);
    results.complete = await completeOnboarding(onProgress);
    
    return {
      success: true,
      results,
      storageAvailable: results.storage?.storageAvailable ?? false,
      encryptionEnabled: results.encryption?.encryptionEnabled ?? false
    };
  } catch (error) {
    console.error('Onboarding failed:', error);
    return {
      success: false,
      results,
      error: error.message
    };
  }
};

/**
 * Reset local storage (user-initiated)
 */
export const resetLocalStorage = async (confirmed = false) => {
  if (!confirmed) {
    throw new Error('RESET_NOT_CONFIRMED');
  }

  try {
    // Clear all IndexedDB data
    const databases = await indexedDB.databases?.() || [];
    
    for (const db of databases) {
      if (db.name?.startsWith('RettVerk')) {
        indexedDB.deleteDatabase(db.name);
      }
    }

    // Clear localStorage items related to RettVerk
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('rettverk_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    return {
      success: true,
      message: 'Lokal lagring er tilbakestilt. Du vil bli bedt om å gjennomføre onboarding på nytt.'
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Generate a unique device ID (stored locally only)
 */
const getDeviceId = async () => {
  let deviceId = await getSetting('device_id');
  
  if (!deviceId) {
    deviceId = 'device_' + crypto.randomUUID();
    await saveSetting('device_id', deviceId);
  }
  
  return deviceId;
};

/**
 * Check if File System Access API is supported
 */
export const checkFileSystemSupport = () => {
  return 'showDirectoryPicker' in window;
};

/**
 * Liste over kjente systemfiler som indikerer at mappen er en systembeskyttet rotmappe
 */
const SYSTEM_FILES = [
  '.DS_Store',           // macOS
  'desktop.ini',         // Windows
  '.Trash',              // macOS Trash
  '.Trash-1000',         // Linux Trash
  '.Spotlight-V100',     // macOS Spotlight
  '.fseventsd',          // macOS File System Events
  '.TemporaryItems',     // macOS
  '.DocumentRevisions-V100', // macOS
  'Thumbs.db',           // Windows
  '$RECYCLE.BIN',        // Windows Recycle Bin
  'System Volume Information', // Windows
  '.localized',          // macOS
  'Icon\r',              // macOS custom folder icons
];

/**
 * Sjekk om den valgte mappen inneholder systemfiler (indikerer rotmappe)
 * @param {FileSystemDirectoryHandle} dirHandle - Directory handle å sjekke
 * @returns {Object} Result med isSystemFolder boolean og funnet systemfil
 */
const checkForSystemFiles = async (dirHandle) => {
  try {
    const foundSystemFiles = [];
    
    for await (const entry of dirHandle.values()) {
      const lowerName = entry.name.toLowerCase();
      
      // Sjekk mot kjente systemfiler (case-insensitive for noen)
      for (const sysFile of SYSTEM_FILES) {
        if (entry.name === sysFile || lowerName === sysFile.toLowerCase()) {
          foundSystemFiles.push(entry.name);
        }
      }
      
      // Stopp tidlig hvis vi finner systemfiler
      if (foundSystemFiles.length > 0) {
        break;
      }
    }
    
    return {
      isSystemFolder: foundSystemFiles.length > 0,
      foundFiles: foundSystemFiles
    };
  } catch (error) {
    // Hvis vi ikke kan lese innholdet, anta at det kan være greit
    console.warn('Could not check folder contents:', error);
    return {
      isSystemFolder: false,
      foundFiles: [],
      error: error.message
    };
  }
};

/**
 * Opprett RettVerk undermapper i den valgte mappen
 * @param {FileSystemDirectoryHandle} selectedFolder - Den valgte mappen
 * @returns {Object} Result with success status and created folders
 */
const createRettVerkSubfolders = async (selectedFolder) => {
  try {
    // Opprett undermapper direkte i den valgte mappen
    const bibliotekFolder = await selectedFolder.getDirectoryHandle('bibliotek', { create: true });
    const eksportFolder = await selectedFolder.getDirectoryHandle('eksport', { create: true });
    const tempFolder = await selectedFolder.getDirectoryHandle('temp', { create: true });
    
    return {
      success: true,
      mainFolder: selectedFolder,
      subFolders: {
        bibliotek: bibliotekFolder,
        eksport: eksportFolder,
        temp: tempFolder
      }
    };
  } catch (error) {
    console.error('Error creating RettVerk subfolders:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Automatisk opprett RettVerk 1.2 mappe i brukervalgt overordnet mappe
 * Brukeren velger f.eks. Dokumenter eller Skrivebord, og vi oppretter "RettVerk 1.2" inni den.
 * @returns {Object} Result med success status, mappenavn og handle
 */
export const createRettVerkFolder = async () => {
  if (!checkFileSystemSupport()) {
    return {
      success: false,
      error: 'Enheten din støtter ikke mappevalg. RettVerk vil bruke intern lagring.',
      fallbackToIndexedDB: true
    };
  }

  try {
    // Åpne directory picker - brukeren velger overordnet mappe (f.eks. Dokumenter)
    const parentFolder = await window.showDirectoryPicker({
      mode: 'readwrite',
      startIn: 'documents'
    });

    // Opprett "RettVerk 1.2" mappen inne i den valgte mappen
    const rettverkFolder = await parentFolder.getDirectoryHandle('RettVerk 1.2', { create: true });

    // Opprett undermappene inne i RettVerk 1.2
    const folderResult = await createRettVerkSubfolders(rettverkFolder);
    
    if (!folderResult.success) {
      return {
        success: false,
        error: 'Kunne ikke opprette undermapper: ' + folderResult.error
      };
    }

    // Lagre directory handle i IndexedDB for senere bruk
    await storeDirectoryHandle(rettverkFolder);

    // Lagre metadata om tilkoblingen
    await saveSetting('local_folder_name', 'RettVerk 1.2');
    await saveSetting('local_folder_parent', parentFolder.name);
    await saveSetting('local_folder_connected', true);
    await saveSetting('local_folder_connected_at', new Date().toISOString());
    await saveSetting('local_folder_structure', {
      main: 'RettVerk 1.2',
      parent: parentFolder.name,
      subfolders: ['bibliotek', 'eksport', 'temp']
    });

    return {
      success: true,
      folderName: 'RettVerk 1.2',
      parentFolder: parentFolder.name,
      handle: rettverkFolder,
      subFolders: folderResult.subFolders,
      message: `Mappen "RettVerk 1.2" er opprettet i "${parentFolder.name}".`
    };
  } catch (error) {
    if (error.name === 'AbortError') {
      return {
        success: false,
        error: 'Du avbrøt mappevalget. Du kan velge mappe senere fra innstillinger.',
        userCancelled: true
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Store directory handle in IndexedDB for persistence
 * @param {FileSystemDirectoryHandle} handle - The directory handle to store
 */
const storeDirectoryHandle = async (handle) => {
  try {
    const { initDB, DB_NAME, DB_VERSION } = await import('./localStorageService');
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onsuccess = (event) => {
        const db = event.target.result;
        
        // Sjekk om 'directoryHandles' store eksisterer
        if (!db.objectStoreNames.contains('directoryHandles')) {
          // Kan ikke opprette store uten versjonoppgradering
          // Lagre i settings i stedet
          const tx = db.transaction('settings', 'readwrite');
          const store = tx.objectStore('settings');
          store.put({
            key: 'rettverkFolderHandle',
            value: handle,
            timestamp: new Date().toISOString()
          });
          tx.oncomplete = () => resolve(true);
          tx.onerror = () => reject(tx.error);
        } else {
          const tx = db.transaction('directoryHandles', 'readwrite');
          const store = tx.objectStore('directoryHandles');
          store.put({
            id: 'rettverkFolder',
            handle: handle,
            timestamp: new Date().toISOString()
          });
          tx.oncomplete = () => resolve(true);
          tx.onerror = () => reject(tx.error);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error storing directory handle:', error);
    return false;
  }
};

/**
 * Retrieve stored directory handle from IndexedDB
 * @returns {FileSystemDirectoryHandle|null} The stored handle or null
 */
export const getStoredDirectoryHandle = async () => {
  try {
    const { DB_NAME, DB_VERSION } = await import('./localStorageService');
    
    return new Promise((resolve) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onsuccess = (event) => {
        const db = event.target.result;
        
        // Prøv settings først
        if (db.objectStoreNames.contains('settings')) {
          const tx = db.transaction('settings', 'readonly');
          const store = tx.objectStore('settings');
          const getRequest = store.get('rettverkFolderHandle');
          
          getRequest.onsuccess = () => {
            const result = getRequest.result;
            if (result && result.value) {
              resolve(result.value);
            } else {
              resolve(null);
            }
          };
          
          getRequest.onerror = () => resolve(null);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => resolve(null);
    });
  } catch (error) {
    return null;
  }
};

/**
 * Request access to local folder for RettVerk storage (File System Access API)
 * Brukeren må velge eller opprette en egen mappe (ikke Dokumenter/Skrivebord direkte).
 * Undermappene bibliotek/, eksport/, og temp/ opprettes automatisk i den valgte mappen.
 */
export const requestLocalFolder = async () => {
  if (!checkFileSystemSupport()) {
    return {
      success: false,
      error: 'Enheten din støtter ikke mappevalg. RettVerk vil bruke intern lagring.',
      fallbackToIndexedDB: true
    };
  }

  try {
    // Åpne directory picker - brukeren velger en mappe de har opprettet
    const selectedFolder = await window.showDirectoryPicker({
      mode: 'readwrite',
      startIn: 'documents'
    });

    // Sjekk om den valgte mappen inneholder systemfiler (indikerer rotmappe)
    const systemCheck = await checkForSystemFiles(selectedFolder);
    
    if (systemCheck.isSystemFolder) {
      return {
        success: false,
        error: 'Du kan ikke velge hele Dokumenter eller Skrivebord. Opprett en mappe inni dem, for eksempel "RettVerk 1.2", og velg den.',
        isSystemFolder: true,
        foundSystemFiles: systemCheck.foundFiles
      };
    }

    // Opprett RettVerk-undermappene i den valgte mappen
    const folderResult = await createRettVerkSubfolders(selectedFolder);
    
    if (!folderResult.success) {
      return {
        success: false,
        error: 'Kunne ikke opprette undermapper: ' + folderResult.error
      };
    }

    // Lagre directory handle i IndexedDB for senere bruk
    await storeDirectoryHandle(folderResult.mainFolder);

    // Lagre metadata om tilkoblingen
    await saveSetting('local_folder_name', selectedFolder.name);
    await saveSetting('local_folder_connected', true);
    await saveSetting('local_folder_connected_at', new Date().toISOString());
    await saveSetting('local_folder_structure', {
      main: selectedFolder.name,
      subfolders: ['bibliotek', 'eksport', 'temp']
    });

    return {
      success: true,
      folderName: selectedFolder.name,
      handle: folderResult.mainFolder,
      subFolders: folderResult.subFolders,
      message: `Mappen "${selectedFolder.name}" er klar med undermapper for RettVerk.`
    };
  } catch (error) {
    if (error.name === 'AbortError') {
      return {
        success: false,
        error: 'Du avbrøt mappevalget. Du kan velge mappe senere fra innstillinger.',
        userCancelled: true
      };
    }
    
    // Håndter tilgangsfeil spesifikt
    if (error.name === 'NotAllowedError' || error.message.includes('system')) {
      return {
        success: false,
        error: 'Denne mappen kan ikke brukes. Opprett en ny mappe inni Dokumenter eller Skrivebord og velg den.',
        isSystemFolder: true
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

export default {
  ONBOARDING_STATUS,
  ONBOARDING_STEPS,
  checkOnboardingStatus,
  runOnboarding,
  resetLocalStorage,
  checkFileSystemSupport,
  requestLocalFolder,
  createRettVerkFolder,
  getStoredDirectoryHandle
};
